import type { Vehicle } from './types';

/**
 * Lamp/visibility channels baked into the merged car body (the `surf.w` vertex attribute). The car
 * shader looks each channel up in per-car uniforms, so lamps light up, parts hang loose and the
 * driver appears without any extra meshes or draw calls.
 */
export const CH = {
  STATIC: 0,
  HEAD: 1,
  TAIL: 2,
  IND_FL: 3,
  IND_FR: 4,
  IND_RL: 5,
  IND_RR: 6,
  REVERSE: 7,
  BEACON: 8,
  SIGN: 9,
  /** Seated driver: collapsed unless the car is occupied. */
  DRIVER: 10,
  /** Optional roof/ladder rack: collapsed unless the car has one. */
  RACK: 11,
  /** Roof skin that turns to vinyl (or a contrast colour) per car. */
  ROOF: 12,
  /** Loose parts, each moved by a per-car matrix (uPart[ch - BONNET]). */
  BONNET: 13,
  BUMPER_F: 14,
  BUMPER_R: 15,
  BOOT: 16,
} as const;

/** Number of lamp channels in `uLamp` (indices 0..LAMP_COUNT-1). */
export const LAMP_COUNT = 10;

/** Per-rig memory so indicators keep blinking through the whole turn instead of flickering. */
export interface LampMemory {
  /** -1 = left, +1 = right, 0 = off. */
  side: number;
  hold: number;
}

export function makeLampMemory(): LampMemory {
  return { side: 0, hold: 0 };
}

export interface LampEnv {
  /** 0 (day) .. 1 (night): scales how bright running lamps read. */
  night: number;
  time: number;
  dt: number;
}

/**
 * Fill `out[ch]` with each lamp channel's emissive level for this frame (0 = off, < 0 = broken).
 * Pure apart from `mem`, so it is unit-tested.
 */
export function lampLevels(v: Vehicle, env: LampEnv, mem: LampMemory, out: Float32Array | number[]): void {
  for (let i = 0; i < LAMP_COUNT; i++) out[i] = 0;
  const wreck = v.condition === 'wrecked';
  const frontBroken = wreck || v.damage.front >= 0.35;
  const rearBroken = wreck || v.damage.rear >= 0.35;
  const tel = v.telemetry;
  const occupied = v.occupant !== null && !v.destroyed;
  const lightsOn = v.headlights && !v.destroyed;
  const night = Math.min(1, Math.max(0, env.night));

  // Indicators: signal while steering into a turn at town speed, keep going briefly after it.
  const speed = Math.abs(tel.forwardSpeed);
  let want = 0;
  if (occupied && speed > 0.3 && speed < 14 && Math.abs(tel.steer) > 0.35) want = tel.steer > 0 ? 1 : -1;
  if (want !== 0) {
    mem.side = want;
    mem.hold = 1.1;
  } else if (mem.hold > 0) {
    mem.hold -= env.dt;
    if (mem.hold <= 0) mem.side = 0;
  }
  const hazards = occupied && speed < 1 && (v.condition === 'smoking' || v.condition === 'heavySmoke');
  const blink = env.time * 1.6 - Math.floor(env.time * 1.6) < 0.5 ? 2.4 : 0;

  // Lens glow: enough to read through the bloom at night without a white flare round the car.
  out[CH.HEAD] = lightsOn ? 0.3 + 1.6 * night : 0;
  out[CH.TAIL] = occupied && tel.braking ? 2.5 : lightsOn ? 0.22 + 0.55 * night : 0.04;
  const left = hazards || mem.side < 0 ? blink : 0;
  const right = hazards || mem.side > 0 ? blink : 0;
  // Local +X is the car's left; IND_FL/RL sit on the left flank.
  out[CH.IND_FL] = left;
  out[CH.IND_RL] = left;
  out[CH.IND_FR] = right;
  out[CH.IND_RR] = right;
  out[CH.REVERSE] = occupied && tel.reversing ? 2.2 : 0;
  out[CH.BEACON] = v.siren && !v.destroyed ? 3.8 : 0;
  out[CH.SIGN] = v.destroyed ? 0 : v.isPolice ? (v.siren || lightsOn ? 0.9 + 0.7 * night : 0.15) : 0.5 + 0.9 * night;

  if (frontBroken) out[CH.HEAD] = out[CH.IND_FL] = out[CH.IND_FR] = -1;
  if (rearBroken) out[CH.TAIL] = out[CH.IND_RL] = out[CH.IND_RR] = out[CH.REVERSE] = -1;
  if (wreck) out[CH.BEACON] = out[CH.SIGN] = -1;
}

/** Loose-part state derived from the damage model (visual only). */
export interface LooseParts {
  /** Bonnet lift (rad), front edge up. */
  bonnet: number;
  /** Boot lid lift (rad), rear edge up. */
  boot: number;
  /** Bumper droop (rad) around the end that stays attached; sign picks the end that lets go. */
  bumperF: number;
  bumperR: number;
}

export function looseParts(v: Vehicle, hangSide: 1 | -1, out: LooseParts): LooseParts {
  const d = v.damage;
  const wreck = v.condition === 'wrecked';
  out.bonnet = d.front >= 0.6 ? Math.min(0.32, 0.12 + (d.front - 0.6) * 0.6) : wreck ? 0.08 : 0;
  out.boot = d.rear >= 0.6 ? Math.min(0.3, 0.1 + (d.rear - 0.6) * 0.6) : 0;
  // A side hit near one end also lets that end of the bumper go.
  const side = d.left > d.right ? 1 : d.right > d.left ? -1 : hangSide;
  out.bumperF = d.front >= 0.5 ? side * Math.min(0.3, 0.1 + (d.front - 0.5) * 0.5) : 0;
  out.bumperR = d.rear >= 0.5 ? -side * Math.min(0.3, 0.1 + (d.rear - 0.5) * 0.5) : 0;
  return out;
}

/** Glass state per pane (0 front, 1 rear, 2 left, 3 right): 0 intact, 1 cracked, 2 gone. */
export function paneState(v: Vehicle, out: number[]): number[] {
  const d = v.damage;
  if (v.condition === 'wrecked') {
    out[0] = out[1] = out[2] = out[3] = 2;
    return out;
  }
  const st = (x: number, crack: number, gone: number): number => (x >= gone ? 2 : x >= crack ? 1 : 0);
  out[0] = st(d.front, 0.45, 0.9);
  out[1] = st(d.rear, 0.45, 0.85);
  out[2] = st(d.left, 0.35, 0.7);
  out[3] = st(d.right, 0.35, 0.7);
  return out;
}
